import type { VisitDto } from '@aawas/contracts';
import { Bike, Car, CheckCircle2, Package, ShieldAlert, User, Wrench, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useDecide, useVisits } from '../lib/queries';
import { usePollMs } from '../lib/realtime';
import { isResident, useSession } from '../lib/session';
import { Button, Card, Empty, ErrorNote, KIND_LABEL, PageHeader, Segmented, Spinner, StatusBadge, VISIT_TONE, fmtDateTime, timeAgo } from '../ui';
import { useToast } from '../ui/toast';

const KIND_ICON: Record<string, typeof User> = { guest: User, delivery: Package, cab: Car, service: Wrench, other: Bike };

export function VisitTitle({ v }: { v: VisitDto }) {
  const Icon = KIND_ICON[v.kind] ?? User;
  return (
    <div className="flex min-w-0 items-center gap-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0">
        <div className="truncate font-semibold">{v.visitorName}</div>
        <div className="truncate text-sm text-slate-600">
          {KIND_LABEL[v.kind]}
          {v.company ? ` · ${v.company}` : ''} · Flat {v.unitLabel}
          {v.vehicleNo ? ` · ${v.vehicleNo}` : ''}
        </div>
      </div>
    </div>
  );
}

/** Live countdown to when a pending request turns into "no response". */
function Deadline({ iso }: { iso: string | null }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const left = Math.max(0, Math.round((new Date(iso).getTime() - now) / 1000));
  return <span className="text-xs font-medium text-amber-800">{left > 0 ? `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')} left` : 'Timed out'}</span>;
}

/** Big, hard-to-miss decision card. Used on the resident home screen and the deep-linked decision page. */
export function DecisionCard({ v }: { v: VisitDto }) {
  const decide = useDecide();
  const toast = useToast();
  const act = async (decision: 'approve' | 'reject') => {
    try {
      await decide.mutateAsync({ id: v.id, decision });
      toast.success(decision === 'approve' ? `${v.visitorName} approved` : `${v.visitorName} rejected`);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'VISIT_ALREADY_DECIDED') toast.show({ title: 'Already answered', body: e.message, tone: 'info' });
      else toast.error(e);
    }
  };
  return (
    <Card className="border-l-4 border-amber-400">
      <div className="flex items-start justify-between gap-2">
        <VisitTitle v={v} />
        <Deadline iso={v.decisionDeadline} />
      </div>
      {v.purpose && <p className="mt-2 text-sm text-slate-700">{v.purpose}</p>}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Button variant="danger" size="lg" onClick={() => act('reject')} disabled={decide.isPending}>
          <XCircle className="h-5 w-5" aria-hidden /> Reject
        </Button>
        <Button variant="success" size="lg" onClick={() => act('approve')} disabled={decide.isPending}>
          <CheckCircle2 className="h-5 w-5" aria-hidden /> Approve
        </Button>
      </div>
    </Card>
  );
}

export function VisitRow({ v, to }: { v: VisitDto; to?: string }) {
  const body = (
    <Card className="flex items-center justify-between gap-3 !p-3">
      <VisitTitle v={v} />
      <div className="shrink-0 text-right">
        <StatusBadge map={VISIT_TONE} status={v.status} />
        <div className="mt-1 text-xs text-slate-500">{timeAgo(v.createdAt)}</div>
      </div>
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

/** Full history list. Residents see their flat; admins and guards see everyone. */
export function VisitorsScreen() {
  const { current } = useSession();
  const poll = usePollMs();
  const [tab, setTab] = useState<'recent' | 'earlier'>('recent');
  const q = useVisits({ limit: 100 }, poll);

  const all = q.data?.items ?? [];
  const isRecent = (v: VisitDto) => Date.now() - new Date(v.createdAt).getTime() < 86_400_000;
  const items = all.filter((v) => (tab === 'recent' ? isRecent(v) : !isRecent(v)));
  const pending = all.filter((v) => v.status === 'pending' && isResident(current.role));

  return (
    <div className="space-y-4">
      <PageHeader title="Visitors" subtitle={isResident(current.role) ? `Flat ${current.unitLabel}` : current.communityName} />
      {pending.map((v) => <DecisionCard key={v.id} v={v} />)}
      <Segmented value={tab} onChange={setTab} options={[{ value: 'recent', label: 'Last 24 hours' }, { value: 'earlier', label: 'Earlier' }]} />
      {q.isLoading ? (
        <Spinner />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => q.refetch()} />
      ) : items.length === 0 ? (
        <Empty title={tab === 'recent' ? 'No visitors in the last 24 hours' : 'No earlier visitors'} hint="When someone arrives at the gate they will show up here." icon={<ShieldAlert className="h-8 w-8" />} />
      ) : (
        <div className="space-y-2">
          {items.map((v) => (
            <VisitRow key={v.id} v={v} to={`/visit/${v.id}`} />
          ))}
        </div>
      )}
    </div>
  );
}

export function VisitDetailScreen({ id, visit }: { id: string; visit: VisitDto | undefined }) {
  const { current } = useSession();
  if (!visit) return <Spinner />;
  return (
    <div className="space-y-4">
      <PageHeader title={visit.visitorName} subtitle={`Flat ${visit.unitLabel} · ${fmtDateTime(visit.createdAt)}`} />
      {visit.status === 'pending' && isResident(current.role) && <DecisionCard v={visit} />}
      <Card className="space-y-3 text-sm">
        <Row k="Status" v={<StatusBadge map={VISIT_TONE} status={visit.status} />} />
        <Row k="Type" v={KIND_LABEL[visit.kind]} />
        {visit.company && <Row k="Company" v={visit.company} />}
        {visit.visitorPhone && <Row k="Phone" v={visit.visitorPhone} />}
        {visit.vehicleNo && <Row k="Vehicle" v={visit.vehicleNo} />}
        {visit.purpose && <Row k="Purpose" v={visit.purpose} />}
        {visit.decidedByName && <Row k="Decided by" v={`${visit.decidedByName}${visit.decidedAt ? ` · ${fmtDateTime(visit.decidedAt)}` : ''}`} />}
        {visit.decisionNote && <Row k="Note" v={visit.decisionNote} />}
        {visit.enteredAt && <Row k="Entered" v={fmtDateTime(visit.enteredAt)} />}
        {visit.exitedAt && <Row k="Left" v={fmtDateTime(visit.exitedAt)} />}
        <span className="sr-only">{id}</span>
      </Card>
    </div>
  );
}

const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-slate-500">{k}</span>
    <span className="text-right font-medium">{v}</span>
  </div>
);
